import { Row, Col, Card, CardBody } from "reactstrap"
import "./FuelUsageSummary.css"
import { useState } from "react"

// Helper function to format date
const formatDate = dateStr => {
  if (!dateStr) return "-"
  const date = new Date(dateStr)
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

// Reusable Stat Card with modern minimal design
const StatCard = ({ title, value, subtitle, icon, trend, color = "primary" }) => (
  <Card className="border-0 rounded-3 shadow-sm hover-lift transition-all mb-0">
    <CardBody className="p-3">
      <div className="d-flex align-items-center justify-content-between">
        <div className="flex-grow-1">
          <div className="text-muted small fw-medium mb-1">{title}</div>
          <div className="d-flex align-items-baseline gap-2">
            <h4 className="mb-0 fw-bold">{value}</h4>
            {trend && (
              <span className={`badge bg-${trend.color}-subtle text-${trend.color} small`} title={trend.tooltip}>
                {trend.value}
              </span>
            )}
          </div>
          {subtitle && <div className="text-muted small mt-1">{subtitle}</div>}
        </div>
        <div style={{ width: "2rem", height: "2rem" }} className={`d-flex align-items-center justify-content-center rounded-circle p-2 bg-${color}-subtle`}>
          <i className={`bx ${icon} text-${color} fs-5`}></i>
        </div>
      </div>
    </CardBody>
  </Card>
)

// Compact value display component
const ValueItem = ({ icon, label, value, className = "", tooltip }) => (
  <div className={`d-flex align-items-center gap-2 small ${className}`} title={tooltip}>
    <i className={`bx ${icon} text-muted`}></i>
    <span className="text-muted">{label}:</span>
    <span className="fw-medium">{value}</span>
  </div>
)

// Totals component - simplified to only show fuel quantity
// Totals component - now includes stock and remaining fuel
const TotalStats = ({ totals = {} }) => {
  const totalFuelQty = parseFloat(totals.total_fuel_qty ?? 0)
  const totalFuelStock = parseFloat(totals.total_fuel_stock ?? 0)
  const remainingFuelStock = parseFloat(totals.remaining_fuel_stock ?? 0)

  const stats = [
    {
      title: "Total Fuel Stock",
      value: `${totalFuelStock.toLocaleString()} L`,
      subtitle: "Total fuel available in stock",
      icon: "bx-cylinder",
      color: "info",
    },
    {
      title: "Fuel Used",
      value: `${totalFuelQty.toLocaleString()} L`,
      subtitle: "Fuel consumed by vehicles",
      icon: "bx-gas-pump",
      color: "warning",
    },
    {
      title: "Remaining Fuel",
      value: `${remainingFuelStock.toLocaleString()} L`,
      subtitle: "Current fuel remaining in stock",
      icon: "bx-droplet",
      color: "success",
    },
  ]

  return (
    <Row className="g-2 mb-4">
      {stats.map((stat, key) => (
        <Col md="4" key={"total_" + key}>
          <StatCard {...stat} />
        </Col>
      ))}
    </Row>
  )
}


// Period stats component with growth & previous - simplified for available data
const PeriodStats = ({ summary = {}, comparison_basis = {} }) => {
  const periodConfigs = [
    { 
      title: "Today", 
      icon: "bx-calendar", 
      data: summary.today, 
      color: "warning",
      comparison: comparison_basis?.today || "Compared to yesterday"
    },
    { 
      title: "This Week", 
      icon: "bx-calendar-week", 
      data: summary.weekly, 
      color: "info",
      comparison: comparison_basis?.weekly || "Compared to previous week"
    },
    { 
      title: "This Month", 
      icon: "bx-calendar", 
      data: summary.monthly, 
      color: "primary",
      comparison: comparison_basis?.monthly || "Compared to previous month"
    },
    { 
      title: "This Year", 
      icon: "bx-calendar-alt", 
      data: summary.yearly, 
      color: "success",
      comparison: comparison_basis?.yearly || "Compared to previous year"
    },
  ]

  const formatGrowth = (growthData, currentData, previousData, periodType, comparisonText) => {
    if (!growthData || growthData.fuel_qty === null) return null
    const value = growthData.fuel_qty
    const trend = growthData.trend
    const currentQty = currentData?.total_fuel_qty || 0
    const previousQty = previousData?.total_fuel_qty || 0
    const tooltip = previousQty === 0
      ? `New ${periodType.toLowerCase()} data - no previous records`
      : `${comparisonText}: ${previousQty.toLocaleString()} L → ${currentQty.toLocaleString()} L`
    return { value: `${value > 0 ? "+" : ""}${value}%`, color: trend === "up" ? "success" : trend === "down" ? "danger" : "secondary", tooltip }
  }

  return (
    <Row className="g-2">
      {periodConfigs.map((period, key) => {
        const data = period.data || {}
        const fuelQty = parseFloat(data?.total_fuel_qty ?? 0)
        const fuelTrend = formatGrowth(
          data?.growth, 
          data, 
          data?.previous_period, 
          period.title.replace("This ", ""),
          period.comparison
        )

        return (
          <Col md="3" key={"period_" + key}>
            <Card className="border-0 rounded-3 shadow-sm hover-lift transition-all">
              <CardBody className="p-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h6 className="text-muted mb-0 small fw-medium">{period.title}</h6>
                  <div style={{ width: "2rem", height: "2rem" }} className={`d-flex align-items-center justify-content-center rounded-circle p-1 bg-${period.color}-subtle`}>
                    <i className={`bx ${period.icon} text-${period.color} fs-6`}></i>
                  </div>
                </div>

                <div className="d-flex align-items-baseline justify-content-between mb-2">
                  <h4 className="mb-0 fw-bold">{fuelQty.toLocaleString()} L</h4>
                  {fuelTrend && (
                    <span 
                      className={`badge bg-${fuelTrend.color}-subtle text-${fuelTrend.color} small`} 
                      title={fuelTrend.tooltip}
                    >
                      {fuelTrend.value}
                    </span>
                  )}
                </div>

                <div className="space-y-1">
                  <ValueItem 
                    icon="bx-calendar" 
                    label="Period" 
                    value={`${formatDate(data?.from)} - ${formatDate(data?.to)}`} 
                    tooltip="Date range for this period" 
                  />

                  {data?.previous_period && (
                    <div className="pt-1 border-top">
                      <ValueItem 
                        icon="bx-history" 
                        label="Previous" 
                        value={`${data.previous_period.total_fuel_qty?.toLocaleString() || 0} L`} 
                        tooltip={`Previous period: ${data.previous_period.total_fuel_qty?.toLocaleString() || 0} L from ${formatDate(data.previous_period.from)} to ${formatDate(data.previous_period.to)}`} 
                      />
                    </div>
                  )}

                  <div className="pt-1">
                    <div className="text-muted small" title={period.comparison}>
                      <i className="bx bx-info-circle me-1"></i>
                      {period.comparison}
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        )
      })}
    </Row>
  )
}

// Main FuelUsageSummary component
const FuelUsageSummary = ({ totals = {}, summary = {}, comparison_basis = {} }) => {
  const [show, setShow] = useState(false)

  return (
    <>
      <div className="mb-2 d-flex justify-content-end">
        <button className="btn btn-outline-primary btn-sm" onClick={() => setShow(prev => !prev)}>
          {show ? "Hide Summary ▲" : "Show Summary ▼"}
        </button>
      </div>
      {show && (
        <>
          <TotalStats totals={totals} />
          <PeriodStats summary={summary} comparison_basis={comparison_basis} />
        </>
      )}
    </>
  )
}

export default FuelUsageSummary